import { config, endpoints } from '../config/env'
import type { NivelDeIngles, SoftwareRequerido } from '@/features/rh/lib/catalogosDeRequisicion'
import type { CandidatoInterno } from '@/features/rh/lib/candidatosInternos'
import { httpRequest } from '@/shared/lib/http'
import { filterOutMasterUsers } from '@/shared/lib/userFilters'
import type {
  Role,
  Permission,
  UserRBACResponse,
  AssignRoleRequest,
  AssignPermissionRequest,
  RoleAssignmentResponse,
  PermissionAssignmentResponse
} from '../types/rbac.types'

// Types
export interface LoginRequest {
  email: string
  password: string
  accessModule?: 'almacen' | 'rh' | 'proveedores'
}

export interface LoginResponse {
  access_token: string
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface UserProfile {
  id: string
  email: string
  firstName: string
  lastName: string
  isActive: boolean
  roles?: Role[] // Roles múltiples del sistema anterior
  directPermissions?: Permission[] // Permisos directos del usuario
  isAreaManager?: boolean
  canAssignSalaries?: boolean
  // Permite activar el bypass de expediente de un proveedor (SP-213). Viene en
  // /auth/login y /auth/profile.
  canBypassSupplierDocumentValidation?: boolean
  /** CR-02: recibe el aviso de baja de herramienta por correo. */
  receivesToolDecommissionEmail?: boolean
  rbac?: {
    userId: string
    email: string
    roles: Array<{
      key: string
      name: string
      permissions: string[]
    }>
    directPermissions: string[]
    effectivePermissions: string[]
    isMaster: boolean
  }
  // ⭐ NUEVOS CAMPOS - Fase 1: Relación Usuarios ↔ Áreas
  areaId?: number
  area?: {
    id: number
    nombre: string
    descripcion?: string
    activa: boolean
  }
  currentModuleId?: string
  createdAt: string
  updatedAt: string
}

export interface UpdateUserRequest {
  firstName: string
  lastName: string
  isActive: boolean
  /** Clave del rol. El back lo acepta como string en UpdateUserDto. */
  role?: string
  areaId?: number
  isAreaManager?: boolean
  canAssignSalaries?: boolean
  canBypassSupplierDocumentValidation?: boolean
  /** CR-02: recibe el aviso de baja de herramienta por correo. */
  receivesToolDecommissionEmail?: boolean
}

export interface CreateUserRequest {
  email: string
  password: string
  firstName: string
  lastName: string
  areaId?: number | null
  isAreaManager?: boolean
  canAssignSalaries?: boolean
  canBypassSupplierDocumentValidation?: boolean
  /** CR-02: recibe el aviso de baja de herramienta por correo. */
  receivesToolDecommissionEmail?: boolean
}

export interface DeleteUserResponse {
  message: string
  /**
   * false cuando el usuario tenia historial y el backend lo desactivo en vez
   * de borrarlo. La lista lo necesita para saber si quitar la fila o solo
   * actualizarla.
   */
  eliminado?: boolean
}


// Module types
export interface ModuleResponse {
  id: string
  name: string
  description: string
  version: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface UserModuleAssignment {
  userId: string
  moduleId: string
  permissions: string[]
  isActive: boolean
  module?: ModuleResponse
}

export interface AssignModuleRequest {
  isActive: boolean
  permissions: string[]
}

// Project types
export interface ProjectResponse {
  id: string
  name: string
  description: string
  startDate: string
  endDate: string
  status: 'active' | 'completed' | 'paused' | 'cancelled'
  moduleId?: string
  createdAt: string
  updatedAt: string
}

export interface UserProjectAssignment {
  userId: string
  projectId: string
  permissions: string[]
  isActive: boolean
  project?: ProjectResponse
}

export interface AssignProjectRequest {
  isActive: boolean
  permissions: string[]
}

export interface ApiError {
  message: string | string[]
  error: string
  statusCode: number
}

export interface SystemSettingsResponse {
  id: string
  logoUrl: string | null
  primaryColor: string
  secondaryColor: string
  footerText: string
  isSingleton: boolean
  createdAt: string
  updatedAt: string
}

export interface UpdateSystemSettingsDto {
  logoUrl?: string
  primaryColor: string
  secondaryColor: string
  footerText: string
}

// Company/Empresa types
export interface Company {
  id: number
  nombre: string
}

export interface CompanyResponse {
  id: number
  nombre: string
  descripcion?: string
  activa: boolean
  createdAt?: string
  updatedAt?: string
}

// Audit Log types
export interface AuditLog {
  id: string
  userId: string
  apiRoute: string              // Ruta de la API llamada
  requestData: string           // Datos de la petición (JSON string)
  responseData: string          // Datos de la respuesta (JSON string)
  timestamp: string             // Timestamp de la acción
  module: string                // Módulo del sistema (auth, users, etc.)
  statusCode: number            // Código de respuesta HTTP
  httpMethod: string            // Método HTTP (GET, POST, etc.)
  actionDescription: string     // Descripción de la acción
  ipAddress: string             // IP del usuario
  userAgent: string             // Navegador/dispositivo
  user: {
    id: string
    firstName: string
    lastName: string
    email: string
    isActive: boolean
    role: string
    createdAt: string
    updatedAt: string
  }
}

export interface AuditLogResponse {
  data: AuditLog[]
  total: number
  page: number
  limit: number
}

// Audit Filter types
export interface AuditFilters {
  userId?: string
  module?: string
  actionDescription?: string
  httpMethod?: string
  statusCode?: number
  ipAddress?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  limit?: number
}

export interface RecentAuditResponse {
  data: AuditLog[]
  total: number
}

// Job Request Status types
// 🔄 ACTUALIZADO: 30 Oct 2025 - Nuevo flujo de aprobación de dos etapas
export const JobRequestStatus = {
  PENDIENTE_VALIDACION_PM: 'pendiente_pre_aprobacion',
  PRE_APROBADA: 'pre_aprobada', // ⭐ NUEVO
  EN_REVISION_SALARIOS: 'pendiente_aprobacion_salario',
  EN_REVISION: 'en_revision',
  DEVUELTA: 'devuelta',
  DEVUELTA_RH: 'devuelta_rh', // ⭐ NUEVO
  APROBADA: 'vacante_activa',
  RECHAZADA: 'rechazada',
  CUBIERTA: 'cubierta'
} as const

export type JobRequestStatusType = typeof JobRequestStatus[keyof typeof JobRequestStatus]

// Enums adicionales para el nuevo flujo
// ⭐ NUEVO: 30 Oct 2025
export const GradoAcademico = {
  SECUNDARIA: 'secundaria',
  PREPARATORIA: 'preparatoria',
  NIVEL_TECNICO: 'nivel_tecnico',
  LICENCIATURA: 'licenciatura',
  MAESTRIA: 'maestria',
  DOCTORADO: 'doctorado'
} as const

export type GradoAcademicoType = typeof GradoAcademico[keyof typeof GradoAcademico]

// ⚠️ ACTUALIZADO: 30 Oct 2025 - Renombrado de Jornada a TipoContrato
export const TipoContrato = {
  TIEMPO_COMPLETO: 'tiempo_completo',
  MEDIO_TIEMPO: 'medio_tiempo',
  PRACTICAS: 'practicas', // ⭐ NUEVO
  POR_PROYECTO: 'por_proyecto' // ⚠️ Reemplaza 'por_horas' y 'temporal'
} as const

export type TipoContratoType = typeof TipoContrato[keyof typeof TipoContrato]

// ⭐ ACTUALIZADO: 30 Oct 2025 - Agregado nuevo valor
export const ProfileType = {
  OPERATIVO: 'operativo',
  TECNICO: 'tecnico',
  ADMINISTRATIVO: 'administrativo',
  POR_PROYECTO: 'por_proyecto' // ⭐ NUEVO
} as const

export type ProfileTypeValue = typeof ProfileType[keyof typeof ProfileType]

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  // ⭐ NUEVOS CAMPOS - Fase 1: Relación Usuarios ↔ Áreas
  areaId?: number // FK a Area - puede ser null
  area?: {
    id: number
    nombre: string
    descripcion?: string
    activa: boolean
  }
}

export interface JobRequest {
  id: number
  // Backend field names (exactos según la respuesta)
  puestoSolicitar: string
  areaVacante: string
  sede: string
  tipoPerfil: string // Backend devuelve string, puede estar vacío

  // ⚠️ RENOMBRADO: 30 Oct 2025 - jornada → tipoContrato
  tipoContrato: TipoContratoType

  prioridad: 'alta' | 'media' | 'baja' | 'urgente'
  justificacionNecesidad: string // ⚠️ Ahora obligatorio
  numeroPlazas: number
  fechaRequeridaInicio: string
  habilidadesMinimas: string
  salarioMinimo: string | null  // Backend devuelve string o null
  salarioMaximo: string | null  // Backend devuelve string o null
  prestaciones: string | null
  motivo: 'nueva' | 'reemplazo'
  /** A quién se reemplaza. Solo se captura cuando el motivo es «reemplazo». */
  previousEmployeeName: string | null
  entrevistaFinalSolicitante: boolean
  /** Si el puesto necesita hardware o software preparado antes del ingreso. */
  requiresHardwareSoftware: boolean
  /** Si el puesto necesita equipo y escritorio preparados antes del ingreso. */
  requiresWorkstation: boolean
  /** Centro de costo al que se carga la plaza. */
  costCenter: string | null
  /** Cuánto viaja el puesto, de 0 a 100. Nulo es sin capturar; 0 es no viaja. */
  travelPercentage: number | null
  otraInformacion: string | null
  status: string // Backend puede devolver string vacío
  plazasCubiertas?: boolean // Indica si las plazas ya fueron cubiertas
  comentariosHr: string | null
  informacionAdicionalSolicitada: string | null
  respuestaSolicitante: string | null
  fechaAprobacion: string | null
  fechaRechazo: string | null
  fechaDevolucion: string | null
  fechaRespuesta: string | null
  motivoRechazo: string | null
  motivoDevolucion: string | null
  observacionesFinales: string | null

  // ⭐ NUEVOS CAMPOS: 30 Oct 2025 - Requisitos académicos y profesionales
  gradoAcademico: GradoAcademicoType
  añosExperiencia: number
  certificacionesRequeridas: string[] | null
  otrasCertificaciones: string | null
  // Idioma y paqueteria (RH-02)
  englishLevel?: NivelDeIngles | null
  requiredSoftware?: SoftwareRequerido[]
  /** Candidatos internos considerados, hasta 2 (RH-01). */
  internalCandidates?: CandidatoInterno[]

  // ⭐ NUEVOS CAMPOS: 30 Oct 2025 - Flujo de aprobación de dos etapas
  // Pre-aprobación PM
  preAprobadoPorUserId: number | null
  preAprobadoPor: User | null
  fechaPreAprobacion: string | null
  comentariosPM: string | null

  // Asignación de salarios
  salarioAsignadoPorUserId: number | null
  salarioAsignadoPor: User | null
  fechaAsignacionSalario: string | null
  comentariosAsignacionSalarios: string | null

  // Devolución a RH
  devueltaRHPorUserId: number | null
  devueltaRHPor: User | null
  fechaDevolucionRH: string | null
  motivoDevolucionRH: string | null

  // ⭐ NUEVO CAMPO - Fase 2: Filtros por área
  areaId?: number // Área a la que pertenece la requisición
  area?: {
    id: number
    nombre: string
    descripcion?: string
    activa: boolean
  }
  revisadoPor: {
    id: string
    email: string
    firstName: string
    lastName: string
    isActive: boolean
    role: string
    createdAt: string
    updatedAt: string
  } | null
  fechaRevision: string | null
  solicitante: {
    id: string
    email: string
    firstName: string
    lastName: string
    isActive: boolean
    role: string
    createdAt: string
    updatedAt: string
  } | null
  fechaCreacion: string
  fechaActualizacion: string

  // Relación con proyecto
  proyectoId?: string | null
  proyecto?: {
    id: string
    name: string
    description?: string
  } | null
}

export interface CreateJobRequestData {
  // Campos obligatorios (nombres del backend)
  puestoSolicitar: string
  areaVacante: string
  sede: string
  tipoPerfil: ProfileTypeValue // ⚠️ ACTUALIZADO: 30 Oct 2025 - Incluye 'por_proyecto'

  // ⚠️ RENOMBRADO: 30 Oct 2025 - jornada → tipoContrato
  tipoContrato: TipoContratoType

  habilidadesMinimas: string
  fechaRequeridaInicio: string
  prioridad: 'alta' | 'media' | 'baja' | 'urgente'
  motivo: 'nueva' | 'reemplazo'
  /** A quién se reemplaza. Solo se captura cuando el motivo es «reemplazo». */
  previousEmployeeName?: string
  numeroPlazas: number

  // ⚠️ AHORA OBLIGATORIO: 30 Oct 2025
  justificacionNecesidad: string

  // ⭐ NUEVOS CAMPOS OBLIGATORIOS: 30 Oct 2025
  gradoAcademico: GradoAcademicoType
  añosExperiencia: number | undefined

  // Campos opcionales
  salarioMinimo?: number
  salarioMaximo?: number
  prestaciones?: string
  entrevistaFinalSolicitante?: boolean
  /** Si el puesto necesita hardware o software preparado antes del ingreso. */
  requiresHardwareSoftware?: boolean
  /** Si el puesto necesita equipo y escritorio preparados antes del ingreso. */
  requiresWorkstation?: boolean
  /** Centro de costo al que se carga la plaza. */
  costCenter?: string | null
  /** Cuánto viaja el puesto, de 0 a 100. Nulo es sin capturar; 0 es no viaja. */
  travelPercentage?: number | null
  otraInformacion?: string
  proyectoId?: string

  // ⭐ NUEVOS CAMPOS OPCIONALES: 30 Oct 2025
  certificacionesRequeridas?: string[]
  otrasCertificaciones?: string

  // Idioma y paqueteria (RH-02)
  englishLevel?: NivelDeIngles | null
  requiredSoftware?: SoftwareRequerido[]
  /** Candidatos internos considerados, hasta 2 (RH-01). */
  internalCandidates?: CandidatoInterno[]

  // ⭐ NUEVO CAMPO - Fase 2: Filtros por área
  areaId?: number // Opcional/Requerido según rol (ver documentación)
}

export interface UpdateJobRequestData {
  status?: string
  puestoSolicitar?: string
  areaVacante?: string
  sede?: string
  tipoPerfil?: ProfileTypeValue // ⚠️ ACTUALIZADO: 30 Oct 2025

  // ⚠️ RENOMBRADO: 30 Oct 2025 - jornada → tipoContrato
  tipoContrato?: TipoContratoType

  habilidadesMinimas?: string
  salarioMinimo?: number
  salarioMaximo?: number
  prestaciones?: string
  motivo?: 'nueva' | 'reemplazo'
  /** A quién se reemplaza. Solo se captura cuando el motivo es «reemplazo». */
  previousEmployeeName?: string
  fechaRequeridaInicio?: string
  prioridad?: 'alta' | 'media' | 'baja' | 'urgente'
  entrevistaFinalSolicitante?: boolean
  /** Si el puesto necesita hardware o software preparado antes del ingreso. */
  requiresHardwareSoftware?: boolean
  /** Si el puesto necesita equipo y escritorio preparados antes del ingreso. */
  requiresWorkstation?: boolean
  /** Centro de costo al que se carga la plaza. */
  costCenter?: string | null
  /** Cuánto viaja el puesto, de 0 a 100. Nulo es sin capturar; 0 es no viaja. */
  travelPercentage?: number | null
  /** Candidatos internos considerados, hasta 2 (RH-01). */
  internalCandidates?: CandidatoInterno[]
  numeroPlazas?: number
  justificacionNecesidad?: string
  otraInformacion?: string
  proyectoId?: string

  // ⭐ NUEVOS CAMPOS: 30 Oct 2025
  gradoAcademico?: GradoAcademicoType
  añosExperiencia?: number
  certificacionesRequeridas?: string[]
  otrasCertificaciones?: string
}

// DTOs para las nuevas acciones de aprobación
export interface ApproveJobRequestDto {
  comentariosHr?: string
}

export interface RejectJobRequestDto {
  /**
   * El motivo del rechazo. Se llama así porque así lo pide el servidor; antes
   * aquí viajaba como `razonRechazo` y el rechazo fallaba siempre con un 400
   * de validación, porque esa propiedad ni existe en el DTO.
   */
  comentariosHr: string
}

export interface ReturnJobRequestDto {
  comentariosHr: string  // ✅ Comentarios de RH (obligatorio)
  informacionAdicionalSolicitada?: string  // ✅ Información adicional solicitada (opcional)
}

export interface RespondToReturnedRequestDto extends Partial<CreateJobRequestData> {
  respuestaSolicitante?: string
}

// ⭐ NUEVOS DTOs: 30 Oct 2025 - Flujo de dos etapas
export interface PreApproveJobRequestDto {
  comentariosPM?: string
}

export interface AssignSalariesDto {
  salarioMinimo: number
  salarioMaximo: number
  prestaciones?: string
  comentariosRH?: string
}

export interface FinalApproveJobRequestDto {
  comentarios?: string
}

export interface ReturnToRHDto {
  motivo: string // Obligatorio
}

// Interfaz para las solicitudes aprobadas (vacantes activas)
export interface ApprovedJobRequest {
  id: number
  puestoSolicitar: string        // puesto en la respuesta original
  areaVacante: string           // area en la respuesta original
  justificacionNecesidad: string // descripcion en la respuesta original
  habilidadesMinimas: string    // perfilRequerido en la respuesta original
  salarioMinimo: number | null
  salarioMaximo: number | null
  fechaRequeridaInicio: string  // fechaEstimadaInicio en la respuesta original
  prioridad: 'alta' | 'media' | 'baja' | 'urgente' // urgencia en la respuesta original
  numeroPlazas: number
  /** Las plazas ya se cubrieron: sigue activa, pero ya no contrata. */
  plazasCubiertas?: boolean
  status: 'vacante_activa'
  solicitante: {
    id: string
    firstName: string           // nombre en la respuesta original será firstName + lastName
    lastName: string
    email: string
  } | null
  fechaCreacion: string
  candidateCount: number
}

// Interfaces para candidatos
export interface CandidateUser {
  id: string
  email: string
  firstName: string
  lastName: string
  isActive: boolean
  role: string
  createdAt: string
  updatedAt: string
}

export interface Candidate {
  id: string
  fullName: string
  phone: string
  email: string
  age: number
  profesion: string | null
  certificaciones: string | null
  residenceCity: string | null
  /** decimal del servidor: llega como cadena. Pasar por formatoDeSueldo. */
  salaryExpectation: number | string | null
  /** Alta manual en Seleccionado sin la evaluación todavía (SRH-309). */
  evaluationPending?: boolean
  evaluationUrl?: string | null
  experienceYears: number
  experienceDescription: string
  cvUrl: string | null
  privacyConsent: boolean
  status:
    | 'pre_registro'
    | 'registrado'
    | 'pendiente'
    | 'en_proceso'
    | 'entrevista_rh'
    | 'pre_seleccionado'
    | 'pendiente_cierre'        // RH-03.1: aprobado por el solicitante, a la espera del cierre de RH
    | 'pendiente_direccion'     // RH-03.1: RH lo mando a validacion de Direccion
    | 'decision_direccion'      // SRH-311: Direccion decidio, RH confirma
    | 'entrevista_solicitante'
    | 'seleccionado'
    | 'rechazado_evaluacion_rh'
    | 'contratado'
    | 'rechazado'
  jobRequestId: number
  userId: string
  emailConfirmationToken: string | null
  emailConfirmed: boolean
  createdAt: string
  updatedAt: string
  jobRequest: {
    id: number
    areaVacante: string
    sede: string
    puestoSolicitar: string
    jornada: string
    prioridad: string
    justificacionNecesidad: string
    numeroPlazas: number
    fechaRequeridaInicio: string
    habilidadesMinimas: string
    salarioMinimo: number | null
    salarioMaximo: number | null
    prestaciones: string | null
    motivo: string
    entrevistaFinalSolicitante: boolean
    tipoPerfil: string
    otraInformacion: string
    status: string
    comentariosHr: string | null
    informacionAdicionalSolicitada: string | null
    respuestaSolicitante: string | null
    fechaAprobacion: string | null
    fechaRechazo: string | null
    fechaDevolucion: string | null
    fechaRespuesta: string | null
    motivoRechazo: string | null
    motivoDevolucion: string | null
    observacionesFinales: string | null
    revisadoPor: {
      id: string
      email: string
      firstName: string
      lastName: string
      isActive: boolean
      role: string
      createdAt: string
      updatedAt: string
    } | null
    fechaRevision: string | null
    solicitante: {
      id: string
      email: string
      firstName: string
      lastName: string
      isActive: boolean
      role: string
      createdAt: string
      updatedAt: string
    } | null
    fechaCreacion: string
    fechaActualizacion: string
  }
  user: CandidateUser
  // ── Datos del descarte (los llena el back al descartar) ──
  /** Estatus que tenía el candidato justo antes de ser descartado. */
  descarteEtapa?: string | null
  descarteMotivo?: string | null
  descarteComentario?: string | null
  descartadoPor?: { id: string; firstName?: string; lastName?: string } | null
  descartadoEn?: string | null
  // ── Origen declarado y recomendación (RH-06) ──
  /**
   * Por cuál puerta entró el registro. Lo pone el back, no se captura; no
   * confundir con `source`, que es lo que el candidato declara.
   */
  captureMethod?: 'public_form' | 'manual_entry'
  sourceId?: number | null
  source?: {
    id: number
    name: string
    isReferral: boolean
  } | null
  /** El nombre tal como lo escribió quien llenó el formulario. */
  referredByText?: string | null
  /** A quién del directorio se vinculó. Independiente del texto declarado. */
  referredByEmployeeId?: number | null
  referredByEmployee?: {
    id: number
    user?: { firstName?: string; lastName?: string } | null
  } | null
  referralLinkedBy?: { id: string; firstName?: string; lastName?: string } | null
  referralLinkedAt?: string | null
}

export interface JobRequestStatistics {
  total: number
  pendienteValidacionRh: number
  enRevision: number
  devueltas: number
  aprobadas: number
  rechazadas: number
  porArea: { [key: string]: number }
  porUrgencia: { [key: string]: number }
  porTipoPerfil: { [key: string]: number }
  porMotivo: { [key: string]: number }
}

// Base API class
/**
 * Una postulacion anterior de la misma persona (RH-07).
 */
export interface PreviousApplication {
  candidateId: string
  jobRequestId: number
  position?: string | null
  area?: string | null
  appliedAt: string
  status: string
  stageReached?: string | null
  discardReason?: string | null
  discardComment?: string | null
  /** Por que se considera la misma persona: correo, telefono o ambos. */
  matchedBy: ('email' | 'phone')[]
}

/**
 * Un comentario del tab Comentarios de la ficha (SRH-321). El back junta los de
 * la entrevista de RH, la entrevista del solicitante, la aprobacion o rechazo
 * desde la ficha y el descarte.
 */
export interface CandidateComment {
  author: string | null
  stage:
    | 'hr_interview'
    | 'requester_interview'
    | 'requester_decision'
    | 'discard'
    | 'returned'
    | 'direction_decision'
  /** Nulo en la entrevista de RH. */
  action: 'approved' | 'rejected' | 'discarded' | null
  /** Motivo del descarte, del catalogo. */
  reason: string | null
  text: string
  date: string | null
}

class ApiService {
  private baseUrl: string

  constructor() {
    this.baseUrl = config.apiBaseUrl
  }

  // Normalize array-like API responses that may be wrapped, e.g. { data: T[] } or { roles: T[] }
  private normalizeArray<T>(payload: any, keys: string[] = ['data', 'roles', 'items', 'result', 'results']): T[] {
    if (Array.isArray(payload)) return payload as T[]
    if (payload && typeof payload === 'object') {
      for (const k of keys) {
        const v = (payload as any)[k]
        if (Array.isArray(v)) return v as T[]
      }
    }
    return []
  }

  // Fetch all pages for endpoints that return paginated objects
  private async fetchAllPages<T>(
    endpoint: string,
    options?: {
      arrayKeys?: string[]
      pageParam?: string
      limitParam?: string
      pageSize?: number
      maxPages?: number
      initialPage?: number
      extraQuery?: Record<string, string | number | boolean>
    }
  ): Promise<T[]> {
    const {
      arrayKeys = ['data', 'items', 'results', 'permissions', 'roles'],
      pageParam = 'page',
      limitParam = 'limit',
      pageSize = 200,
      maxPages = 50,
      initialPage = 1,
      extraQuery = {}
    } = options || {}

    const acc: T[] = []
    let page = initialPage
    for (let i = 0; i < maxPages; i++) {
      const params = new URLSearchParams({
        [pageParam]: String(page),
        [limitParam]: String(pageSize),
        ...Object.fromEntries(Object.entries(extraQuery).map(([k, v]) => [k, String(v)])),
      })
      const sep = endpoint.includes('?') ? '&' : '?'
      const url = `${endpoint}${sep}${params.toString()}`
      const resp = await this.request<any>(url)
      const chunk = this.normalizeArray<T>(resp, arrayKeys)
      acc.push(...chunk)

      // Stop condition: based on total or short page
      const total = typeof resp?.total === 'number' ? resp.total : undefined
      if (total !== undefined) {
        if (acc.length >= total) break
      } else if (chunk.length < pageSize) {
        break
      }

      page += 1
    }
    return acc
  }

  /**
   * Delega en el cliente HTTP compartido. Antes tenia su propia copia del
   * manejo de token, de errores y del 401.
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Un 401 al iniciar sesion son credenciales incorrectas, no una sesion
    // expirada: el formulario lo muestra en linea y no hay que redirigir.
    const esLogin = endpoint === endpoints.auth.login
    const datos = await httpRequest<T>(endpoint, {
      ...options,
      // Este cliente es el del sistema interno: siempre manda el token del
      // staff. Sin esto, el cliente elegía el token por la ruta del navegador y
      // en el portal del proveedor mandaba el del proveedor a un endpoint
      // interno: el servidor contestaba 401 y la app cerraba sesiones.
      token: 'staff',
      skipAuthRedirect: esLogin,
    })
    return datos as T
  }

  // Auth methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>(endpoints.auth.login, {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async logout(): Promise<void> {
    return this.request<void>(endpoints.auth.logout, {
      method: 'POST',
    })
  }

  async getProfile(): Promise<UserProfile> {
    return this.request<UserProfile>(endpoints.auth.profile)
  }

  // El back invalida el resto de sesiones (bump de tokenVersion) y reemite un
  // access_token nuevo; el caller debe guardarlo con saveToken() de inmediato
  // o la siguiente request usa el token viejo (ya invalido) y desloguea.
  async changePassword(data: ChangePasswordRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>(endpoints.auth.changePassword, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // Users methods
  async getUsers(): Promise<UserProfile[]> {
    const users = await this.request<UserProfile[]>(endpoints.users.list)
    return filterOutMasterUsers(users)
  }

   async getAllUsers(): Promise<UserProfile[]> {
    const users = await this.request<UserProfile[]>(endpoints.users.list)
    return users
  }

  async getUserById(id: string): Promise<UserProfile> {
    return this.request<UserProfile>(`${endpoints.users.detail}/${id}`)
  }

  async createUser(data: CreateUserRequest): Promise<UserProfile> {
    return this.request<UserProfile>(endpoints.auth.register, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateUser(id: string, data: UpdateUserRequest): Promise<UserProfile> {
    return this.request<UserProfile>(`${endpoints.users.update}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteUser(id: string): Promise<DeleteUserResponse> {
    return this.request<DeleteUserResponse>(`${endpoints.users.delete}/${id}`, {
      method: 'DELETE',
    })
  }
  // Nuevo método para obtener usuarios por permiso
  async getUsersByPermission(permissionKey: string): Promise<User[]> {
    const users = await this.request<User[]>(`/users/by-permission/${permissionKey}`)
    return filterOutMasterUsers(users)
  }

  // Audit methods
  async getUserAuditHistory(userId: string, page: number = 1, limit: number = 15): Promise<AuditLogResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    })
    return this.request<AuditLogResponse>(`${endpoints.audit.userHistory}/${userId}?${params}`)
  }

  async getRecentAuditHistory(): Promise<RecentAuditResponse> {
    // The backend returns an array directly, not wrapped in an object
    const data = await this.request<AuditLog[]>(endpoints.audit.recent)
    return {
      data: data,
      total: data.length
    }
  }

  async getAuditHistoryWithFilters(filters: AuditFilters): Promise<AuditLogResponse> {
    const params = new URLSearchParams()

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString())
      }
    })

    const queryString = params.toString()
    const url = queryString ? `${endpoints.audit.filtered}?${queryString}` : endpoints.audit.filtered

    // The backend now returns a wrapped object with data, total, page, etc.
    return this.request<AuditLogResponse>(url)
  }

  // Job Requests methods
  async createJobRequest(data: CreateJobRequestData): Promise<JobRequest> {
    return this.request<JobRequest>(endpoints.jobRequests.create, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getJobRequests(status?: string, myRequests?: boolean): Promise<JobRequest[]> {
    let url = endpoints.jobRequests.list
    const params = new URLSearchParams()

    if (status) {
      params.append('status', status)
    }

    if (myRequests) {
      params.append('my-requests', 'true')
    }

    if (params.toString()) {
      url += `?${params.toString()}`
    }

    return this.request<JobRequest[]>(url)
  }

  async getMyJobRequests(): Promise<JobRequest[]> {
    return this.request<JobRequest[]>(endpoints.jobRequests.myRequests)
  }

  /**
   * Vacantes cubiertas que todavía tienen candidatos en proceso (SRH-313). El
   * tablero las suma a las activas para que esos candidatos no desaparezcan.
   */
  async getCoveredJobRequestsWithOpenCandidates(): Promise<
    Array<{ id: number; puestoSolicitar?: string | null; status: string }>
  > {
    return this.request('/job-requests/covered-with-open-candidates')
  }

  /**
   * RH confirma al candidato interno que eligió el solicitante (SRH-313). Con la
   * vacante ya cubierta el servidor advierte; se reintenta con `confirmOverbooking`.
   */
  async confirmInternalCandidate(
    jobRequestId: number,
    confirmOverbooking = false,
  ): Promise<JobRequest> {
    return this.request<JobRequest>(
      `/job-requests/${jobRequestId}/internal-candidates/confirm`,
      {
        method: 'PATCH',
        body: JSON.stringify(confirmOverbooking ? { confirmOverbooking: true } : {}),
      },
    )
  }

  async getJobRequestById(id: string): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}`)
  }

  async updateJobRequest(id: string, data: UpdateJobRequestData): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.update}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteJobRequest(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`${endpoints.jobRequests.delete}/${id}`, {
      method: 'DELETE',
    })
  }

  async getJobRequestStatistics(): Promise<JobRequestStatistics> {
    return this.request<JobRequestStatistics>(endpoints.jobRequests.statistics)
  }

  // Método para obtener las solicitudes aprobadas (vacantes activas)
  async getApprovedJobRequests(): Promise<ApprovedJobRequest[]> {
    return this.request<ApprovedJobRequest[]>('/job-requests/approved')
  }

  /**
   * Alta de un candidato a mano, sin el formulario público (RH-05).
   *
   * Pide cuatro datos y el correo opcional. El correo vacío se omite del
   * cuerpo: mandarlo como cadena vacía haría que el servidor lo tome por un
   * correo mal escrito, y el `forbidNonWhitelisted` del back no perdona campos
   * de más.
   */
  async registrarCandidatoManual(datos: {
    jobRequestId: number
    status: string
    fullName: string
    age: number
    profesion: string
    phone: string
    email?: string
    /** Origen declarado y quien recomendo (RH-06). */
    sourceId?: number
    referredByText?: string
    /** Lo que el formulario publico tambien captura, todo opcional (RH-05). */
    experienceYears?: number
    experienceDescription?: string
    certificaciones?: string
    residenceCity?: string
    salaryExpectation?: number
    cvUrl?: string
    /** Obligatoria para que un alta en Seleccionado tenga efectos (SRH-309). */
    evaluationUrl?: string
  }): Promise<Candidate> {
    const { email, ...resto } = datos
    return this.request<Candidate>('/candidates/registro-manual', {
      method: 'POST',
      body: JSON.stringify({
        ...resto,
        ...(email?.trim() ? { email: email.trim() } : {}),
      }),
    })
  }

  /**
   * Sube la evaluación que le faltaba a un candidato dado de alta en
   * Seleccionado y completa su selección (SRH-309).
   */
  async subirEvaluacionDelCandidato(
    candidateId: string,
    evaluationUrl: string,
  ): Promise<Candidate> {
    return this.request<Candidate>(`/candidates/profile/${candidateId}/evaluation`, {
      method: 'PATCH',
      body: JSON.stringify({ evaluationUrl }),
    })
  }

  // Método para obtener el historial de vacantes completadas
  async getCompletedJobRequests(): Promise<any[]> {
    return this.request<any[]>('/job-requests/completed-report')
  }

  // Método para obtener candidatos de una vacante específica
  async getCandidatesByJobRequest(jobRequestId: number): Promise<Candidate[]> {
    return this.request<Candidate[]>(`/candidates/job-request/${jobRequestId}`)
  }

  /**
   * Candidatos de varias vacantes en una sola llamada.
   *
   * El panel de reclutamiento los necesita todos. Pidiéndolos uno por uno se
   * topaba con el límite de peticiones del servidor y la pantalla se caía con
   * un 429.
   */
  async getCandidatesByJobRequests(jobRequestIds: number[]): Promise<Candidate[]> {
    if (jobRequestIds.length === 0) return []
    return this.request<Candidate[]>(
      `/candidates?jobRequestIds=${jobRequestIds.join(',')}`,
    )
  }

  // Nuevos métodos para el sistema de aprobaciones avanzado
  async reviewJobRequest(id: string): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/review`, {
      method: 'PUT',
    })
  }

  // 🔄 ACTUALIZADO: 18 Nov 2025 - Cambiado a PUT según requerimiento
  async approveJobRequest(id: string, data: ApproveJobRequestDto): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // 🔄 ACTUALIZADO: 18 Nov 2025 - Cambiado a PUT según requerimiento
  async rejectJobRequest(id: string, data: RejectJobRequestDto): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Devuelve una solicitud rechazada al flujo de autorización (SRH-317).
   * El motivo es opcional: el caso típico es deshacer un dedazo.
   */
  async reactivateJobRequest(
    id: string,
    data: { motivo?: string } = {},
  ): Promise<JobRequest> {
    return this.request<JobRequest>(
      `${endpoints.jobRequests.detail}/${id}/reactivate`,
      { method: 'PUT', body: JSON.stringify(data) },
    )
  }

  async returnJobRequest(id: string, data: ReturnJobRequestDto): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/return`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async respondToReturnedRequest(id: string, data: RespondToReturnedRequestDto): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/respond`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // ⭐ NUEVOS MÉTODOS: 30 Oct 2025 - Flujo de aprobación de dos etapas

  /**
   * Pre-aprobar solicitud (Project Manager)
   * Estado requerido: pendiente_validacion_pm → pre_aprobada
   */
  async preApproveJobRequest(id: string, data: PreApproveJobRequestDto): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/pre-approve`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Asignar salarios (Admin RH)
   * Estado requerido: pre_aprobada → en_revision_salarios
   */
  async assignSalaries(id: string, data: AssignSalariesDto): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/assign-salaries`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Aprobación final (Project Manager)
   * Estado requerido: en_revision_salarios → aprobada
   */
  async finalApproveJobRequest(id: string, data: FinalApproveJobRequestDto): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/final-approve`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Devolver a RH (Project Manager)
   * Estado requerido: en_revision_salarios → devuelta_rh
   */
  async returnToRH(id: string, data: ReturnToRHDto): Promise<JobRequest> {
    return this.request<JobRequest>(`${endpoints.jobRequests.detail}/${id}/return-to-rh`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getSystemSettings(): Promise<SystemSettingsResponse> {
    return this.request<SystemSettingsResponse>(endpoints.systemSettings.base)
  }

  async updateSystemSettings(data: UpdateSystemSettingsDto): Promise<SystemSettingsResponse> {
    return this.request<SystemSettingsResponse>(endpoints.systemSettings.base, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Company/Empresa Methods
  async getCompanies(): Promise<Company[]> {
    const response = await fetch(`${config.apiBaseUrl}/catalogos/empresas`, {
      headers: {
        'Authorization': `Bearer ${this.getToken()}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error('Error al cargar empresas')
    }

    const data = await response.json()

    // Manejar diferentes formatos de respuesta
    const empresasArray = Array.isArray(data) ? data : data.data || []

    // Mapear a la estructura simplificada (id, nombre)
    return empresasArray.map((empresa: CompanyResponse) => ({
      id: empresa.id,
      nombre: empresa.nombre,
    }))
  }

  async getCompanyById(id: number): Promise<CompanyResponse> {
    return this.request<CompanyResponse>(`/catalogos/empresas/${id}`)
  }

  // Helper methods
  saveToken(token: string): void {
    localStorage.setItem('access_token', token)
  }

  removeToken(): void {
    localStorage.removeItem('access_token')
  }

  getToken(): string | null {
    return localStorage.getItem('access_token')
  }

  // RBAC Methods

  // Get all available roles
  async getAllRoles(): Promise<Role[]> {
    const resp = await this.request<any>(endpoints.rbac.roles)
    return this.normalizeArray<Role>(resp, ['data', 'roles', 'items'])
  }

  // Get all available permissions
  async getAllPermissions(): Promise<Permission[]> {
    const resp = await this.request<any>(endpoints.rbac.permissions)
    return this.normalizeArray<Permission>(resp, ['data', 'permissions', 'items'])
  }

  // Get all available permissions across all pages
  async getAllPermissionsAll(): Promise<Permission[]> {
    return this.fetchAllPages<Permission>(endpoints.rbac.permissions, {
      arrayKeys: ['data', 'permissions', 'items'],
      pageParam: 'page',
      limitParam: 'limit',
      pageSize: 200,
      maxPages: 100
    })
  }

  // Get system roles only
  async getSystemRoles(): Promise<Role[]> {
    const resp = await this.request<any>(endpoints.rbac.systemRoles)
    return this.normalizeArray<Role>(resp, ['data', 'roles', 'items'])
  }

  // Get complete RBAC summary for a user
  async getUserRBAC(userId: string): Promise<UserRBACResponse> {
    return this.request<UserRBACResponse>(`${endpoints.rbac.userRbacSummary}/${userId}/rbac-summary`)
  }

  // Get user's assigned roles
  async getUserRoles(userId: string): Promise<Role[]> {
    const resp = await this.request<any>(`${endpoints.rbac.userRoles}/${userId}/roles`)
    return this.normalizeArray<Role>(resp, ['data', 'roles', 'items'])
  }

  // Get user's assigned permissions
  async getUserPermissions(userId: string): Promise<Permission[]> {
    const resp = await this.request<any>(`${endpoints.rbac.userPermissions}/${userId}/permissions`)
    return this.normalizeArray<Permission>(resp, ['data', 'permissions', 'items'])
  }

  // Assign roles to user
  async assignRolesToUser(userId: string, roleKeys: string[]): Promise<RoleAssignmentResponse> {
    const payload: AssignRoleRequest = { roleKeys }
    return this.request<RoleAssignmentResponse>(`${endpoints.rbac.userRoles}/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  // Update all user roles (replaces existing roles)
  async updateUserRoles(userId: string, roleKeys: string[]): Promise<UserProfile> {
    const payload = { roleKeys }
    return this.request<UserProfile>(`${endpoints.rbac.userRoles}/${userId}/roles`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
  }

  // Add a single role to user
  async addRoleToUser(userId: string, roleKey: string): Promise<UserProfile> {
    const payload = { roleKey }
    return this.request<UserProfile>(`${endpoints.rbac.userRoles}/${userId}/roles/add`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  // Get available roles for role assignment
  async getAvailableRoles(): Promise<Role[]> {
    const resp = await this.request<any>('/users/available-roles')
    return this.normalizeArray<Role>(resp, ['roles', 'data', 'items'])
  }

  // Get roles catalog (JWT required, no catalog permissions)
  async getRolesCatalog(): Promise<Role[]> {
    const resp = await this.request<any>('/roles/public/all')
    return this.normalizeArray<Role>(resp, ['roles', 'data', 'items'])
  }

  // Assign permissions to user
  async assignPermissionsToUser(userId: string, permissionKeys: string[]): Promise<PermissionAssignmentResponse> {
    const payload: AssignPermissionRequest = { permissionKeys }
    return this.request<PermissionAssignmentResponse>(`${endpoints.rbac.userPermissions}/${userId}/permissions`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  // Remove role from user
  async removeRoleFromUser(userId: string, roleKey: string): Promise<RoleAssignmentResponse> {
    return this.request<RoleAssignmentResponse>(`${endpoints.rbac.userRoles}/${userId}/roles/${roleKey}`, {
      method: 'DELETE'
    })
  }

  // Remove permission from user
  async removePermissionFromUser(userId: string, permissionKey: string): Promise<PermissionAssignmentResponse> {
    return this.request<PermissionAssignmentResponse>(`${endpoints.rbac.userPermissions}/${userId}/permissions/${permissionKey}`, {
      method: 'DELETE'
    })
  }

  // === MODULE MANAGEMENT ===

  // Get all modules
  async getAllModules(): Promise<ModuleResponse[]> {
    return this.request<ModuleResponse[]>('/modules')
  }

  // Get module by ID
  async getModuleById(moduleId: string): Promise<ModuleResponse> {
    return this.request<ModuleResponse>(`/api/modules/${moduleId}`)
  }

  // Get users assigned to module
  async getModuleUsers(moduleId: string): Promise<UserModuleAssignment[]> {
    return this.request<UserModuleAssignment[]>(`/api/modules/${moduleId}/users`)
  }

  // Assign user to module
  async assignUserToModule(userId: string, moduleId: string, data: AssignModuleRequest): Promise<UserModuleAssignment> {
    return this.request<UserModuleAssignment>(`/users/${userId}/modules/${moduleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  // === PROJECT MANAGEMENT ===

  // Get all projects
  async getAllProjects(): Promise<ProjectResponse[]> {
    return this.request<ProjectResponse[]>('/projects')
  }

  // Get project by ID
  async getProjectById(projectId: string): Promise<ProjectResponse> {
    return this.request<ProjectResponse>(`/projects/${projectId}`)
  }

  // Get users assigned to project
  async getProjectUsers(projectId: string): Promise<UserProjectAssignment[]> {
    return this.request<UserProjectAssignment[]>(`/projects/${projectId}/users`)
  }

  // Assign user to project
  async assignUserToProject(userId: string, projectId: string, data: AssignProjectRequest): Promise<UserProjectAssignment> {
    return this.request<UserProjectAssignment>(`/users/${userId}/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  // === USER ASSIGNMENTS BY USER ===

  // Get user's module assignments
  async getUserModules(userId: string): Promise<UserModuleAssignment[]> {
    return this.request<UserModuleAssignment[]>(`/users/${userId}/modules`)
  }

  // Get user's project assignments
  async getUserProjects(userId: string): Promise<UserProjectAssignment[]> {
    return this.request<UserProjectAssignment[]>(`/users/${userId}/projects`)
  }

  // Set active module for current user
  async setActiveModule(moduleId: string): Promise<UserProfile> {
    return this.request<UserProfile>('/users/me/active-module', {
      method: 'POST',
      body: JSON.stringify({ moduleId })
    })
  }

  // === CANDIDATE METHODS ===

  // Get candidate by ID
  async getCandidateById(candidateId: string): Promise<Candidate> {
    return this.request<Candidate>(`${endpoints.candidates.profile}/${candidateId}`)
  }

  /**
   * Postulaciones anteriores de la misma persona (RH-07).
   *
   * La coincidencia la resuelve el back por correo o telefono normalizados, y
   * es informativa: no fusiona registros. `matchedBy` dice en que se baso.
   */
  async getPreviousApplications(candidateId: string): Promise<PreviousApplication[]> {
    return this.request<PreviousApplication[]>(
      `${endpoints.candidates.profile}/${candidateId}/previous-applications`,
    )
  }

  /** Comentarios de todas las etapas, del mas antiguo al mas reciente (SRH-321). */
  async getCandidateComments(candidateId: string): Promise<CandidateComment[]> {
    return this.request<CandidateComment[]>(
      `${endpoints.candidates.profile}/${candidateId}/comments`,
    )
  }

  // Update candidate status
  /**
   * `motivo` solo aplica al descartar: el back lo guarda junto con la etapa de
   * la que salió el candidato, para la tabla de descartados.
   */
  /**
   * `descarte` solo aplica al pasar a un estatus de descarte: el motivo viene de
   * un catálogo y el comentario es texto libre. Se omiten del cuerpo cuando no
   * hay nada que mandar.
   */
  async updateCandidateStatus(
    candidateId: string,
    status: Candidate['status'],
    descarte?: { motivo?: string; comentario?: string },
  ): Promise<Candidate> {
    return this.request<Candidate>(`/candidates/profile/${candidateId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        ...(descarte?.motivo ? { motivo: descarte.motivo } : {}),
        ...(descarte?.comentario ? { comentario: descarte.comentario } : {}),
      })
    })
  }

  /**
   * Devuelve al tablero a un candidato descartado.
   *
   * La vacante se manda siempre porque puede no ser la suya: mientras estuvo
   * descartado, la original pudo cubrirse o cerrarse.
   */
  async reactivateCandidate(
    candidateId: string,
    datos: { jobRequestId: number; motivo?: string },
  ): Promise<Candidate> {
    return this.request<Candidate>(`/candidates/profile/${candidateId}/reactivate`, {
      method: 'PATCH',
      body: JSON.stringify({
        jobRequestId: datos.jobRequestId,
        ...(datos.motivo ? { motivo: datos.motivo } : {}),
      }),
    })
  }

  /**
   * A qué etapa volvería un descartado en la vacante elegida (SRH-320). La
   * decide su historial; el servidor valida de paso que la vacante sirva.
   */
  async getReturnStage(
    candidateId: string,
    jobRequestId: number,
  ): Promise<{ stage: string; reason: string }> {
    return this.request<{ stage: string; reason: string }>(
      `/candidates/profile/${candidateId}/return-stage?jobRequestId=${jobRequestId}`,
    )
  }

  // === DASHBOARD ===

  // Get dashboard metrics (unwrap API envelope { success, data })
  async getDashboardMetrics(): Promise<import('../types/dashboard').DashboardMetrics> {
    const resp = await this.request<import('../types/dashboard').DashboardApiResponse>(endpoints.dashboard.metrics)
    return resp.data
  }
}

// Export singleton instance
export const apiService = new ApiService()
